import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle as FaCheckCircle,
  ChevronLeft as FaChevronLeft,
  FileText as FaFileText,
  Loader2 as FaSpinner,
  Mail as FaMail,
  ShieldCheck as FaShieldCheck,
  UploadCloud as FaUpload,
  X as FaTimes,
} from "lucide-react";
import {
  check2faApi,
  getKybStatusApi,
  initiateKybApi,
  submitCompanyEmailApi,
  uploadKybDocumentApi,
} from "../api/kyb.api";
import splashIcon from "../assets/images/splash-icon.png";
import { showError, showSuccess } from "../utils/toast";
import {
  formatRequiredCheck,
  readVerificationException,
} from "../utils/verificationException";
import { useTheme } from "../context/ThemeContext";
import Button from "../components/ui/Button";
import type { getTheme } from "../config/theme";
import type { ApiError } from "../types/api";
import type { ChangeEvent } from "react";

type ThemeColors = ReturnType<typeof getTheme>["colors"];

interface KycStatus {
  status?: string;
  twoFaEnforced?: boolean;
  companyEmailVerified?: boolean;
  documentVerified?: boolean;
  rejectionReason?: string;
  [key: string]: unknown;
}

/** Error shape for initiateKybApi's "already exists" case, which nests the
 * existing record's id a level deeper than the usual ApiError shape. */
interface KybInitiateError extends ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      kycId?: string;
      id?: string;
      data?: { kycId?: string; kyc?: { id?: string }; id?: string };
      [key: string]: unknown;
    };
  };
}

const STEPS = [
  { id: "2fa", label: "2FA", icon: FaShieldCheck },
  { id: "email", label: "Company Email", icon: FaMail },
  { id: "document", label: "Business Document", icon: FaFileText },
  { id: "review", label: "Under Review", icon: FaCheckCircle },
];

function StepIndicator({ steps, currentStep, theme, subtleBorder }: { steps: typeof STEPS; currentStep: string; kybStatus: KycStatus | null; theme: ThemeColors; subtleBorder: string }) {
  const currentIndex = steps.findIndex((s) => s.id === currentStep);
  return (
    <div className="flex w-full items-center justify-between gap-1 sm:gap-2">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const done = index < currentIndex;
        const active = index === currentIndex;
        return (
          <Fragment key={step.id}>
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all"
                style={{
                  borderColor: done || active ? theme.primary : subtleBorder,
                  background: done ? theme.primary : theme.card,
                  color: done ? "#fff" : active ? theme.primary : theme.textLight,
                }}
              >
                {done ? <FaCheckCircle size={16} /> : <Icon size={16} />}
              </div>
              <span
                className="whitespace-nowrap text-[10px] font-semibold leading-none sm:text-xs"
                style={{ color: active ? theme.primary : done ? theme.textMuted : theme.textLight }}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className="mx-1 h-0.5 min-w-0 flex-1 sm:mx-2"
                style={{ background: index < currentIndex ? theme.primary : subtleBorder }}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

export default function KYBVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Detect return from company email verification link (?emailVerified=true)
  // This path is kept for the ?emailVerified query param fallback
  const emailVerifiedParam = searchParams.get("emailVerified");
  const kycIdFromParam = searchParams.get("kycId");

  const [kycId, setKycId] = useState(() => {
    // Prefer kycId from URL param (returned from email verification link)
    if (kycIdFromParam) {
      localStorage.setItem("kyc_id", kycIdFromParam);
      return kycIdFromParam;
    }
    return localStorage.getItem("kyc_id") || null;
  });
  const [kybStatus, setKybStatus] = useState<KycStatus | null>(null);
  const [currentStep, setCurrentStep] = useState("email");
  const [isInitiating, setIsInitiating] = useState(false);
  const [error, setError] = useState("");

  // Email step
  const [companyEmail, setCompanyEmail] = useState("");
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // 2FA step
  const [twoFaEnabled, setTwoFaEnabled] = useState<boolean | null>(null);
  const [isChecking2fa, setIsChecking2fa] = useState(false);

  // Document step
  const [docFile, setDocFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState("certificate_of_incorporation");
  const [businessName, setBusinessName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [verificationException] = useState(
    location.state?.verificationException || readVerificationException(),
  );

  const pendingPayload = localStorage.getItem("kyb_pending_connect_payload");
  const requiredChecks = verificationException?.requiredChecks || [];

  // ─── Handle ?emailVerified=true query param (fallback path) ───────────────
  useEffect(() => {
    if (emailVerifiedParam === "true" && kycId) {
      window.history.replaceState({}, "", "/kyb-verification");
      showSuccess("Company email verified! Continuing your verification.");
      getKybStatusApi(kycId)
        .then((res) => { setKybStatus(res.data); resolveStep(res.data); })
        .catch(() => setCurrentStep("document"));
    }
  }, [emailVerifiedParam, kycId]);

  // ─── Initiate or resume KYB ───────────────────────────────────────────────
  const initiate = useCallback(async () => {
    setIsInitiating(true);
    setError("");
    try {
      const res = await initiateKybApi();
      // Response shape: { data: { kycId, kyc: {...}, steps: [...] } }
      const id = res?.data?.kycId || res?.data?.kyc?.id || res?.data?.id;
      if (!id) throw new Error("No KYC ID returned from server.");
      const kyc = res?.data?.kyc || res?.data;
      localStorage.setItem("kyc_id", id);
      setKycId(id);
      setKybStatus(kyc);
      resolveStep(kyc);
    } catch (err) {
      // If the backend signals an existing KYB record, extract the ID from the
      // error payload so the user can continue without starting over
      const apiErr = err as KybInitiateError;
      const existingId =
        apiErr?.response?.data?.data?.kycId ||
        apiErr?.response?.data?.data?.kyc?.id ||
        apiErr?.response?.data?.data?.id ||
        apiErr?.response?.data?.kycId ||
        apiErr?.response?.data?.id ||
        null;

      if (existingId) {
        localStorage.setItem("kyc_id", existingId);
        setKycId(existingId);
        try {
          const statusRes = await getKybStatusApi(existingId);
          setKybStatus(statusRes.data);
          resolveStep(statusRes.data);
          return;
        } catch {
          // fall through to error display
        }
      }

      const msg = apiErr?.response?.data?.message || apiErr?.message || "";
      // If the error says "already exists / already initiated", show a gentler message
      if (/already|exists|initiated/i.test(msg) && !existingId) {
        setError(
          "A verification record already exists for your account. If you previously started KYB, please contact support to retrieve your verification ID.",
        );
      } else {
        setError(msg || "Unable to start verification. Please try again.");
      }
    } finally {
      setIsInitiating(false);
    }
  }, []);

  // ─── Determine which step to land on based on status ─────────────────────
  const resolveStep = (kyc: KycStatus | null | undefined) => {
    if (!kyc) return;
    if (kyc.status === "approved" || kyc.status === "rejected") { setCurrentStep("review"); return; }
    if (!kyc.twoFaEnforced) { setCurrentStep("2fa"); return; }
    if (!kyc.companyEmailVerified) { setCurrentStep("email"); return; }
    if (!kyc.documentVerified) { setCurrentStep("document"); return; }
    setCurrentStep("review");
  };

  // ─── On mount: resume or initiate ────────────────────────────────────────
  useEffect(() => {
    const resume = async () => {
      if (kycId) {
        try {
          const res = await getKybStatusApi(kycId);
          setKybStatus(res.data);
          resolveStep(res.data);
        } catch {
          localStorage.removeItem("kyc_id");
          setKycId(null);
          await initiate();
        }
      } else {
        await initiate();
      }
    };
    resume();
  }, []);

  // ─── Handle ?emailVerified=true fallback (query param path) ──────────────
  useEffect(() => {
    if (emailVerifiedParam === "true" && kycId) {
      window.history.replaceState({}, "", "/kyb-verification");
      showSuccess("Company email verified! Continuing your verification.");
      getKybStatusApi(kycId)
        .then((res) => { setKybStatus(res.data); resolveStep(res.data); })
        .catch(() => setCurrentStep("document"));
    }
  }, [emailVerifiedParam, kycId]);

  // ─── Poll for email verification (Tab 1 auto-advances when Tab 2 confirms) ──
  useEffect(() => {
    if (!emailSent || !kycId || currentStep !== "email") return;

    const poll = setInterval(async () => {
      try {
        const res = await getKybStatusApi(kycId);
        if (res.data?.companyEmailVerified) {
          clearInterval(poll);
          setKybStatus(res.data);
          showSuccess("Company email verified! Continuing your verification.");
          resolveStep(res.data);
        }
      } catch {
        // silently ignore poll errors
      }
    }, 5000);

    return () => clearInterval(poll);
  }, [emailSent, kycId, currentStep]);

  const handleSubmitEmail = async () => {
    if (!companyEmail.trim() || isSubmittingEmail) return;
    if (!kycId) {
      setError("Verification session not ready. Please use the retry button above.");
      return;
    }
    setIsSubmittingEmail(true);
    setError("");
    try {
      const redirectUrl = `${window.location.origin}/kyb-verification?emailVerified=true&kycId=${kycId}`;
      await submitCompanyEmailApi(kycId, { email: companyEmail.trim(), redirectUrl });
      setEmailSent(true);
      showSuccess("Verification email sent — check your inbox.");
    } catch (err) {
      setError((err as ApiError)?.response?.data?.message || "Unable to submit email.");
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const handleEmailVerified = async () => {
    // User clicks "I've verified my email" — re-check status
    if (!kycId) return;
    setError("");
    try {
      const res = await getKybStatusApi(kycId);
      setKybStatus(res.data);
      if (res.data?.companyEmailVerified) {
        setCurrentStep("2fa");
      } else {
        setError("Email not verified yet. Please click the link in your inbox first.");
      }
    } catch (err) {
      setError((err as ApiError)?.response?.data?.message || "Unable to check verification status.");
    }
  };

  const handleCheck2fa = async () => {
    if (!kycId || isChecking2fa) return;
    setIsChecking2fa(true);
    setError("");
    try {
      const res = await check2faApi(kycId);
      // required:false means 2FA IS already enabled; required:true means it still needs to be set up
      const enabled = res?.data?.required === false || res?.data?.twoFaEnabled === true || res?.data?.enabled === true;
      setTwoFaEnabled(enabled);
      if (enabled) {
        showSuccess("2FA confirmed — proceeding to document upload.");
        setCurrentStep("email");
      } else {
        setError("2FA is not yet enabled on your account. Enable it in Settings, then check again.");
      }
    } catch (err) {
      setError((err as ApiError)?.response?.data?.message || "Unable to check 2FA status.");
    } finally {
      setIsChecking2fa(false);
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setDocFile(file);
  };

  const handleUploadDocument = async () => {
    if (!docFile || !kycId || isUploading) return;
    setIsUploading(true);
    setError("");
    try {
      const result = await uploadKybDocumentApi(kycId, docFile, {
        documentType,
        businessName: businessName.trim() || undefined,
      });
      // Use backend message and re-fetch status to get accurate review state
      const successMsg = result?.data?.message || result?.message || "Document submitted successfully.";
      showSuccess(successMsg);
      // Re-fetch KYB status so the review step shows the correct state
      try {
        const statusRes = await getKybStatusApi(kycId);
        setKybStatus(statusRes.data);
      } catch {
        // fall through with existing status
      }
      setCurrentStep("review");
      localStorage.removeItem("kyb_pending_connect_payload");
    } catch (err) {
      const msg = (err as ApiError)?.message || (err as ApiError)?.response?.data?.message || "Unable to upload document.";
      setError(msg + ((err as ApiError)?.verificationFailed ? " Make sure the business name matches exactly what's on the document." : ""));
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const { theme, mode } = useTheme();
  // Neutral border for structure — theme.border carries a blue tint meant for
  // accent surfaces; this page uses it only where blue is intentional.
  const subtleBorder = mode === "dark" ? theme.border : "#E7EAF0";

  if (isInitiating) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: theme.background }}>
        <div className="flex flex-col items-center gap-4">
          <FaSpinner size={28} className="animate-spin" style={{ color: theme.primary }} />
          <p className="text-sm font-medium" style={{ color: theme.textMuted }}>Starting verification…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: theme.background }}>
      <div className="mx-auto max-w-lg">
        {/* Back button — top-left, consistent with other auth pages */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-8 text-sm font-semibold"
          style={{ color: theme.primary }}
        >
          ← Back
        </button>

        {/* Header */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src={splashIcon} alt="ToronQ" className="h-10 w-auto" />
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: theme.text }}>
            Business Verification (KYC)
          </h1>
          <p className="text-sm" style={{ color: theme.textMuted }}>
            Complete verification to connect your business and start managing your reputation.
          </p>
        </div>

        {/* Step indicator */}
        <div className="mb-8">
          <StepIndicator steps={STEPS} currentStep={currentStep} kybStatus={kybStatus} theme={theme} subtleBorder={subtleBorder} />
        </div>

        {verificationException && (
          <div className="mb-4 rounded-2xl border p-4" style={{ borderColor: subtleBorder, background: theme.infoBg }}>
            <p className="text-sm font-semibold" style={{ color: theme.primary }}>Verification Trigger</p>
            <p className="mt-1 text-sm font-semibold" style={{ color: theme.text }}>
              {verificationException.triggerReason || "verification_required"}
            </p>
            {requiredChecks.length > 0 && (
              <ul className="mt-2 space-y-1">
                {requiredChecks.map((check: string) => (
                  <li key={check} className="text-sm" style={{ color: theme.textMuted }}>
                    - {formatRequiredCheck(check)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-4 rounded-xl px-4 py-3" style={{ background: theme.dangerBg }}>
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium" style={{ color: theme.danger }}>{error}</p>
              <button type="button" onClick={() => setError("")} className="shrink-0" style={{ color: theme.danger }}>
                <FaTimes size={14} />
              </button>
            </div>
            {!kycId && (
              <Button variant="primary" theme={theme} onClick={initiate} disabled={isInitiating} className="mt-3" style={{ background: theme.danger }}>
                {isInitiating ? "Retrying…" : "Retry Verification Setup"}
              </Button>
            )}
          </div>
        )}

        {/* ── Step: Email ─────────────────────────────────────────────────── */}
        {currentStep === "email" && (
          <div className="rounded-2xl border p-6" style={{ borderColor: subtleBorder, background: theme.card }}>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: theme.infoBg, color: theme.primary }}>
              <FaMail size={22} />
            </div>
            <h2 className="mt-4 text-xl font-semibold" style={{ color: theme.text }}>Verify Company Email</h2>
            <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
              Enter your official business email address. We'll send a verification link.
            </p>

            {!emailSent ? (
              <>
                <input
                  type="email"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmitEmail()}
                  placeholder="info@yourbusiness.com"
                  className="mt-5 w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#0222F1]/40 focus:ring-2 focus:ring-[#0222F1]/10"
                  style={{ borderColor: subtleBorder, background: theme.secondary, color: theme.text }}
                />
                <Button variant="primary" theme={theme} onClick={handleSubmitEmail} disabled={!companyEmail.trim() || isSubmittingEmail} className="mt-4 w-full py-3">
                  {isSubmittingEmail ? "Sending…" : "Send Verification Email"}
                </Button>
              </>
            ) : (
              <div className="mt-5 space-y-4">
                <div className="rounded-xl px-4 py-3" style={{ background: theme.successBg }}>
                  <p className="text-sm font-medium" style={{ color: theme.success }}>
                    Verification email sent to <strong>{companyEmail}</strong>. Click the link in your inbox, then come back here.
                  </p>
                </div>
                <Button variant="primary" theme={theme} onClick={handleEmailVerified} className="w-full py-3">
                  I've Verified My Email →
                </Button>
                <Button variant="secondary" theme={theme} onClick={() => setEmailSent(false)} className="w-full py-3 text-sm">
                  Use a different email
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Step: 2FA ───────────────────────────────────────────────────── */}
        {currentStep === "2fa" && (
          <div className="rounded-2xl border p-6" style={{ borderColor: subtleBorder, background: theme.card }}>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: theme.infoBg, color: theme.primary }}>
              <FaShieldCheck size={22} />
            </div>
            <h2 className="mt-4 text-xl font-semibold" style={{ color: theme.text }}>Enable Two-Factor Authentication</h2>
            <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
              KYB requires 2FA to be active on your account for added security.
            </p>

            {twoFaEnabled === false && (
              <div className="mt-4 rounded-xl px-4 py-3" style={{ background: theme.warningBg }}>
                <p className="text-sm font-medium" style={{ color: theme.warning }}>
                  2FA is not enabled on your account yet. Enable it in Settings, then come back and click "Check again".
                </p>
              </div>
            )}

            <div className="mt-5 space-y-3">
              {twoFaEnabled !== true && (
                <Button variant="secondary" theme={theme} onClick={() => navigate("/dashboard/settings")} className="w-full py-3">
                  Go to Settings → Enable 2FA
                </Button>
              )}
              <Button variant="primary" theme={theme} onClick={handleCheck2fa} disabled={isChecking2fa} className="w-full py-3">
                {isChecking2fa ? "Checking…" : "Check 2FA Status"}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step: Document ──────────────────────────────────────────────── */}
        {currentStep === "document" && (
          <div className="rounded-2xl border p-6" style={{ borderColor: subtleBorder, background: theme.card }}>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: theme.infoBg, color: theme.primary }}>
              <FaFileText size={22} />
            </div>
            <h2 className="mt-4 text-xl font-semibold" style={{ color: theme.text }}>Upload Business Document</h2>
            <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
              Upload a government-issued business registration document, CAC certificate, or equivalent.
            </p>

            {/* Document type */}
            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-medium" style={{ color: theme.textMuted }}>Document Type</label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full rounded-xl border px-4 py-3 text-sm outline-none"
                style={{ borderColor: subtleBorder, background: theme.secondary, color: theme.text }}
              >
                <option value="certificate_of_incorporation">Certificate of Incorporation</option>
                <option value="utility_bill">Utility Bill</option>
                <option value="business_license">Business License</option>
                <option value="tax_certificate">Tax Certificate</option>
                <option value="company_email_verified">Company Email Verified</option>
              </select>
            </div>

            {/* Business name */}
            <div className="mt-3">
              <label className="mb-1.5 block text-sm font-medium" style={{ color: theme.textMuted }}>Business Name on Document</label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g. Acme Corp Ltd"
                className="w-full rounded-xl border px-4 py-3 text-sm outline-none"
                style={{ borderColor: subtleBorder, background: theme.secondary, color: theme.text }}
              />
            </div>

            <div
              onClick={() => fileInputRef.current?.click()}
              className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-4 py-10 transition-colors hover:border-[#0222F1]/50"
              style={{ borderColor: subtleBorder, background: theme.secondary }}
            >
              <FaUpload size={26} color={theme.textLight} />
              {docFile ? (
                <div className="text-center">
                  <p className="text-sm font-semibold" style={{ color: theme.text }}>{docFile.name}</p>
                  <p className="mt-1 text-xs" style={{ color: theme.textLight }}>{(docFile.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm font-medium" style={{ color: theme.textMuted }}>Click to upload or drag and drop</p>
                  <p className="mt-1 text-xs" style={{ color: theme.textLight }}>PDF, JPG, PNG — max 10MB</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {docFile && (
              <button
                onClick={() => setDocFile(null)}
                className="mt-2 text-xs font-semibold hover:opacity-80"
                style={{ color: theme.textLight }}
              >
                Remove file
              </button>
            )}

            <Button variant="primary" theme={theme} onClick={handleUploadDocument} disabled={!docFile || isUploading} className="mt-5 w-full py-3">
              {isUploading ? "Uploading…" : "Submit Document"}
            </Button>
          </div>
        )}

        {/* ── Step: Review ────────────────────────────────────────────────── */}
        {currentStep === "review" && (
          <div className="rounded-2xl border p-6 text-center" style={{ borderColor: subtleBorder, background: theme.card }}>
            {(kybStatus?.status === "approved" || kybStatus?.documentVerified === true) ? (
              <>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.successBg }}>
                  <FaCheckCircle size={26} color={theme.success} />
                </div>
                <h2 className="mt-5 text-xl font-semibold" style={{ color: theme.text }}>Verification Approved!</h2>
                <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
                  Your business has been verified. You can now connect your Google Business Profile.
                </p>
                <Button
                  variant="primary"
                  theme={theme}
                  onClick={() => {
                    localStorage.removeItem("kyc_id");
                    navigate("/connect-google");
                  }}
                  className="mt-6 w-full py-3"
                >
                  Connect Business Now →
                </Button>
              </>
            ) : kybStatus?.status === "rejected" ? (
              <>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.dangerBg }}>
                  <FaTimes size={26} color={theme.danger} />
                </div>
                <h2 className="mt-5 text-xl font-semibold" style={{ color: theme.text }}>Verification Rejected</h2>
                <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
                  {kybStatus?.rejectionReason || "Your documents could not be verified. Please re-submit with a clearer document."}
                </p>
                <Button
                  variant="primary"
                  theme={theme}
                  onClick={() => {
                    localStorage.removeItem("kyc_id");
                    setKycId(null);
                    setCurrentStep("email");
                    initiate();
                  }}
                  className="mt-6 w-full py-3"
                >
                  Start Over
                </Button>
              </>
            ) : (
              <>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.infoBg }}>
                  <FaSpinner size={24} className="animate-spin" color={theme.primary} />
                </div>
                <h2 className="mt-5 text-xl font-semibold" style={{ color: theme.text }}>Under Review</h2>
                <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
                  Your documents have been submitted. Our team will review your business within 1–2 business days. You'll receive an email once approved.
                </p>
                <Button variant="secondary" theme={theme} onClick={() => navigate("/dashboard/overview")} className="mt-6 w-full py-3">
                  Go to Dashboard
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
